import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Paper, Stack, Snackbar, Alert, TextField, Button, Tooltip, Chip, Divider, Link as MuiLink, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { AddLink, InfoOutlined, OpenInNew, Refresh, Launch, Language } from '@mui/icons-material';
import { getMonitorings, addMonitoring } from '../services/devopsAPIService';

const DevopsMonitoring = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState('');
  const [form, setForm] = useState({ monitoringName: '', monitoringUrl: '' });
  const [submitting, setSubmitting] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    (async () => {
      try {
        const data = await getMonitorings();
        if (!mounted) return;
        setList(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length) setSelected(data[0]);
      } catch (e) {
        if (!mounted) return;
        setList([]);
        setSnackbar({ open: true, message: e?.message || 'Failed to fetch monitoring list', severity: 'error' });
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSelect = (e) => {
    setSelected(e.target.value);
  };

  const canSubmit = useMemo(() => {
    return form.monitoringName.trim() !== '' && /^https?:\/\//i.test(form.monitoringUrl.trim());  
  }, [form]);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await addMonitoring({ monitoringName: form.monitoringName.trim(), monitoringUrl: form.monitoringUrl.trim() });
      setSnackbar({ open: true, message: 'Monitoring added successfully', severity: 'success' });
      setForm({ monitoringName: '', monitoringUrl: '' });
      const data = await getMonitorings();
      setList(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length) setSelected(data[0]);
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || 'Failed to add monitoring', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>DevOps Monitoring</Typography>
          <Typography variant="body2" color="text.secondary">
            Centralize quick links to your organization dashboards like Grafana, Kibana, Prometheus, etc.
          </Typography>
        </Box>
        {Boolean(list?.length) && (
          <Chip label={`${list.length} items`} size="small" color="primary" sx={{ fontWeight: 700 }} />
        )}
      </Stack>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="monitoring-select-label">Select</InputLabel>
            <Select
              labelId="monitoring-select-label"
              label="Select"
              value={selected}
              onChange={handleSelect}
              renderValue={(val) => (val ? (val.monitoringName || val.name || '') : '')}
            >
              {list.map((d, idx) => (
                <MenuItem key={d.accountMonitoringId || idx} value={d}>{d.monitoringName || d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Button
              size="medium"
              variant="contained"
              startIcon={<AddLink />}
              sx={{ textTransform: 'none', borderRadius: 999, px: 2.5, height: 40, boxShadow: 'none', whiteSpace: 'nowrap' }}
              onClick={() => { setForm({ monitoringName: '', monitoringUrl: '' }); setOpenAdd(true); }}
            >
              Add Monitoring
            </Button>
            <Tooltip title="Open in new tab">
              <span>
                <IconButton
                  color="default"
                  disabled={!selected}
                  onClick={() => {
                    if (selected?.monitoringUrl || selected?.url) {
                      window.open(selected.monitoringUrl || selected.url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  sx={{ border: '1px solid', borderColor: 'divider', height: 40, width: 40 }}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isLoading ? 'Refreshing...' : 'Refresh list'}>
              <IconButton
                color="default"
                disabled={isLoading}
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    const data = await getMonitorings();
                    setList(Array.isArray(data) ? data : []);
                    if (Array.isArray(data) && data.length) setSelected(data[0]);
                    setSnackbar({ open: true, message: 'List refreshed', severity: 'success' });
                  } catch (e) {
                    setSnackbar({ open: true, message: e?.message || 'Failed to refresh', severity: 'error' });
                  } finally {
                    setIsLoading(false);
                  }
                }}
                sx={{ border: '1px solid', borderColor: 'divider', height: 40, width: 40 }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, bgcolor: 'grey.50' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Language fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {selected ? (selected.monitoringName || selected.name) : 'Preview'}
            </Typography>
          </Stack>
          {selected && (
            <MuiLink href={selected.monitoringUrl || selected.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {selected.monitoringUrl || selected.url}
              <Launch sx={{ fontSize: 16 }} />
            </MuiLink>
          )}
        </Box>
        <Divider />
        <Box sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
          {selected ? (
            <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto' }}>
              <iframe
                title={selected.monitoringName || selected.name}
                src={selected.monitoringUrl || selected.url}
                width="100%"
                style={{ border: 0, minHeight: '82vh' }}
              />
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">Select a monitoring entry to preview it here.</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Add Monitoring</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              autoFocus
              size="small"
              fullWidth
              label="Monitoring Name"
              placeholder="e.g., Grafana"
              value={form.monitoringName}
              onChange={(e) => setForm((s) => ({ ...s, monitoringName: e.target.value }))}
            />
            <TextField
              size="small"
              fullWidth
              label="Monitoring URL"
              placeholder="https://grafana.example.com"
              value={form.monitoringUrl}
              onChange={(e) => setForm((s) => ({ ...s, monitoringUrl: e.target.value }))}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Tip: Use meaningful names (e.g., "Grafana - Prod") and ensure the URL is accessible from your network.
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => { await handleSubmit(); setOpenAdd(false); }} disabled={!canSubmit || submitting}>
            {submitting ? 'Saving...' : 'Add Monitoring'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevopsMonitoring;
